import { errorFrom } from "@/lib/errors";
import { costChangedCredits, creditsErrorCode, formatCredits } from "@/lib/creditsApi";
import { isSubscriptionRequired } from "@/lib/billingApi";
import { useTranslation } from "react-i18next";
import { HeroCharacter } from "@/components/brand/HeroCharacter";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAccessGate } from "@/lib/access";
import { toast } from "sonner";
import { Clapperboard, Loader2, Pencil, Sparkles, Timer } from "lucide-react";
import { Layout } from "@/components/hud/Layout";
import { Button } from "@/components/primitives/Button";
import { FormatPicker } from "@/components/create/FormatPicker";
import { ReferencePicker, type LocalReference } from "@/components/direct/ReferencePicker";
import type { AspectRatio } from "@/lib/api";
import {
  addDirectReference,
  createSingleProject,
  deleteDirectReference,
  directProjectPath,
  generateDirectProject,
  prepareSingleProject,
  type DirectProject,
} from "@/lib/directApi";

const MAX_REFERENCES = 5;
type Step = null | "create" | "upload" | "prepare" | "generate";
const newToken = () => (typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

// Create V4 quality-first — prompt + optional references + format → server-side optimizer chooses the duration (5–10 s)
// and the exact cost BEFORE the paid confirmation → ONE video (anchor + Kling I2V with native audio).
export default function Create() {
  const { t, i18n } = useTranslation();
  const [prompt, setPrompt] = useState("");
  const [aspect, setAspect] = useState<AspectRatio>("9:16");
  const [refs, setRefs] = useState<LocalReference[]>([]);
  const [prepared, setPrepared] = useState<DirectProject | null>(null); // plan with duration + cost (no paid call yet)
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState<Step>(null);
  const clientToken = useRef(newToken()); // backend idempotency: double click / retry never creates twice
  const projectId = useRef<string | null>(null);
  const navigate = useNavigate();
  const { guard } = useAccessGate();

  const syncReferences = async (id: string) => {
    let failed = 0;
    for (const r of refs) {
      if (r.serverId) continue;
      try {
        const p = await addDirectReference(id, r.file);
        const added = p.references[p.references.length - 1];
        r.serverId = added?.id;
      } catch {
        failed += 1;
      }
    }
    return failed;
  };

  const prepare = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = prompt.trim();
    if (!text) {
      toast.error(t("create.promptRequired"));
      return;
    }
    if (busy) return;
    if (!(await guard(true))) return; // auth + active subscription BEFORE project creation / reference upload
    setBusy("create");
    try {
      if (!projectId.current) {
        projectId.current = (await createSingleProject({ prompt: text, aspect_ratio: aspect, client_token: clientToken.current })).id;
      }
    } catch (err: any) {
      toast.error(errorFrom(err, "direct.createFailed"));
      setBusy(null);
      return;
    }
    const id = projectId.current;
    setBusy("upload");
    const failed = await syncReferences(id);
    if (failed) {
      toast.error(t("direct.uploadFailed"));
      setBusy(null);
      return;
    }
    setBusy("prepare");
    try {
      setPrepared(await prepareSingleProject(id, { prompt: text, aspect_ratio: aspect }));
    } catch (err: any) {
      toast.error(errorFrom(err, "direct.analyzeFailed"));
    } finally {
      setBusy(null);
    }
  };

  const removeReference = async (item: LocalReference) => {
    if (item.serverId && projectId.current) {
      try {
        await deleteDirectReference(projectId.current, item.serverId);
      } catch {
        /* removed locally anyway; the next prepare re-syncs */
      }
    }
  };

  const edit = () => setPrepared(null); // plan becomes stale → must prepare again (same project, no duplicates)

  const generate = async () => {
    if (!(await guard(true))) return;
    setConfirming(false);
    if (busy || !prepared) return;
    setBusy("generate");
    try {
      await generateDirectProject(prepared.id, prepared.credits_required ?? null);
    } catch (err: any) {
      const code = creditsErrorCode(err);
      if (code === "COST_CHANGED") {
        const c = costChangedCredits(err);
        toast.error(t("credits.costChanged", { old: c?.old ?? 0, n: c?.next ?? 0 }));
        setPrepared({ ...prepared, credits_required: c?.next ?? prepared.credits_required }); // ask for a new confirmation
        setBusy(null);
        return;
      }
      if (isSubscriptionRequired(err)) toast.error(t("billing.subscriptionRequired"), { action: { label: t("billing.subscribe"), onClick: () => navigate("/billing") } });
      else if (code === "INSUFFICIENT_CREDITS") toast.error(t("credits.insufficient"), { action: { label: t("billing.buyCredits"), onClick: () => navigate("/billing#packs") } });
      else toast.error(errorFrom(err, "direct.generateFailed"));
      setBusy(null);
      return;
    }
    window.dispatchEvent(new Event("credits:refresh"));
    navigate(directProjectPath(prepared.id));
  };

  const stepLabel =
    busy === "create" ? t("direct.stepCreate") : busy === "upload" ? t("direct.stepUpload") : busy === "prepare" ? t("direct.stepPrepare") : t("direct.stepGenerate");
  const locked = !!busy || !!prepared;

  return (
    <Layout>
      <main data-testid="create-page" className="relative mx-auto max-w-5xl px-6 pb-32 pt-16 sm:px-10 lg:pt-20">
        <div aria-hidden className="pointer-events-none absolute -top-24 left-1/2 h-[480px] w-[900px] -translate-x-1/2 rounded-full bg-[#2E7BFF]/[0.12] blur-[150px]" />
        <HeroCharacter variant="create" className="-right-40 top-0" />

        <span className="relative inline-flex items-center gap-3 font-display text-xs font-semibold uppercase tracking-[0.34em] text-[#7FD4FF]">
          <span className="h-px w-8 bg-gradient-to-r from-[#2E7BFF] to-transparent" />
          {t("direct.eyebrowSingle")}
        </span>
        <h1 className="relative mt-6 font-display text-4xl font-bold uppercase leading-[0.98] tracking-wide text-foreground sm:text-5xl lg:text-6xl">
          {t("create.title")}
          <br />
          <span className="text-neon">{t("direct.titleB")}</span>
        </h1>
        <p className="relative mt-4 max-w-md text-sm text-muted-foreground sm:text-base">{t("direct.singleSubtitle", { s: 10 })}</p>

        <div className="glass brackets relative mt-12 rounded-2xl p-6 sm:p-9">
          <span className="bk" />
          <div aria-hidden className="pointer-events-none absolute inset-x-12 top-0 h-px bg-gradient-to-r from-transparent via-[#7FD4FF]/70 to-transparent" />
          <form data-testid="create-form" onSubmit={prepare} className="space-y-9">
            <div className="space-y-3">
              <label htmlFor="create-prompt" className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-[0.26em] text-[#7FD4FF]">
                <span>{t("create.prompt")}</span>
                <span data-testid="create-prompt-count" className="font-display text-xs tracking-wider text-muted-foreground">
                  {t("direct.chars", { n: prompt.length.toLocaleString() })}
                </span>
              </label>
              <textarea
                id="create-prompt"
                name="prompt"
                data-testid="create-prompt-input"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={10}
                disabled={locked}
                placeholder={t("direct.placeholder")}
                className="field-premium min-h-[220px] resize-y p-5 text-base leading-relaxed disabled:opacity-60 sm:text-[17px]"
              />
            </div>

            <ReferencePicker items={refs} onChange={setRefs} onRemove={removeReference} max={MAX_REFERENCES} disabled={locked} />

            <div className="space-y-3">
              <span className="block text-[11px] font-semibold uppercase tracking-[0.26em] text-[#7FD4FF]">{t("create.formatLabel")}</span>
              <FormatPicker value={aspect} onChange={setAspect} disabled={locked} />
            </div>

            {!prepared ? (
              <div className="flex flex-col gap-5 border-t border-[#2E7BFF]/15 pt-8 sm:flex-row sm:items-center sm:justify-between">
                <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
                  <Sparkles className="h-3.5 w-3.5 text-[#7FD4FF]" /> {t("direct.noCostHint")}
                </span>
                <button type="submit" data-testid="create-submit-btn" disabled={!!busy || !prompt.trim()} className="cta-hero text-base">
                  <span className="inline-flex items-center gap-3">
                    {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Timer className="h-5 w-5" />}
                    {busy ? stepLabel : t("direct.analyzeCta")}
                  </span>
                </button>
              </div>
            ) : (
              <div data-testid="create-prepared" className="space-y-6 border-t border-[#2E7BFF]/15 pt-8">
                <dl className="grid gap-4 text-sm sm:grid-cols-2">
                  <div className="glass rounded-xl px-4 py-3 !shadow-none">
                    <dt className="text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">{t("direct.optimizedDuration")}</dt>
                    <dd data-testid="create-duration" className="mt-1 font-display text-2xl font-bold tracking-wide text-[#7FD4FF]">{prepared.duration_seconds} s</dd>
                  </div>
                  <div className="glass rounded-xl px-4 py-3 !shadow-none">
                    <dt className="text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">{t("credits.cost")}</dt>
                    <dd data-testid="create-cost" className="mt-1 font-display text-2xl font-bold tracking-wide text-foreground">
                      {t("credits.amount", { n: formatCredits(prepared.credits_required ?? 0, i18n.language) })}
                      <span className="ml-2 text-[10px] font-medium normal-case tracking-normal text-muted-foreground">({t("direct.includesAnchorAlways")})</span>
                    </dd>
                  </div>
                </dl>
                <div className="space-y-1 text-xs text-muted-foreground">
                  <p data-testid="create-duration-note" className="text-[#3DDC84]">{t("direct.autoDurationNote")}</p>
                  <p>{t("direct.autoDurationNote2")}</p>
                  <p className="inline-flex items-center gap-2"><Sparkles className="h-3.5 w-3.5 text-[#7FD4FF]" /> {t("direct.qualityPipeline")}</p>
                </div>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <Button type="button" variant="ghost" size="sm" data-testid="create-edit-btn" disabled={!!busy} onClick={edit}>
                    <Pencil className="h-3.5 w-3.5" /> {t("common.edit")}
                  </Button>
                  <button type="button" data-testid="create-generate-btn" disabled={!!busy} onClick={() => setConfirming(true)} className="cta-hero text-base">
                    <span className="inline-flex items-center gap-3">
                      {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Clapperboard className="h-5 w-5" />}
                      {busy ? stepLabel : t("direct.generateOne")}
                    </span>
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </main>

      {confirming && prepared && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setConfirming(false)}>
          <div className="glass-panel w-full max-w-md rounded-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid="create-confirm">
            <h3 className="font-display text-base font-semibold uppercase tracking-wide text-foreground">{t("direct.confirmTitle")}</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {t("direct.confirmSingle", { seconds: prepared.duration_seconds, aspect: prepared.aspect_ratio, cost: t("credits.amount", { n: formatCredits(prepared.credits_required ?? 0, i18n.language) }) })}{" "}
              {t("common.startsAfterConfirm")}
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setConfirming(false)} data-testid="create-confirm-cancel">{t("common.cancel")}</Button>
              <Button size="sm" onClick={generate} data-testid="create-confirm-accept">{t("common.confirmGenerate")}</Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
